import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  Code,
  Flex,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaUser, FaInfo, FaCode } from 'react-icons/fa';
import JalaliDateDisplay from './JalaliDateDisplay';

/**
 * مودال نمایش جزئیات فعالیت
 * @param {Object} props - props کامپوننت
 * @param {boolean} props.isOpen - وضعیت باز بودن مودال
 * @param {Function} props.onClose - تابع بستن مودال
 * @param {Object} props.activity - اطلاعات فعالیت
 */
const ActivityDetailModal = ({ isOpen, onClose, activity }) => {
  if (!activity) return null;

  // انتخاب آیکون بر اساس نوع فعالیت
  const getActivityIcon = (type) => {
    switch (type) {
      case 'appointment':
        return FaCalendarAlt;
      case 'client':
        return FaUser;
      case 'consultant':
        return FaUser;
      case 'user':
        return FaUser;
      case 'payment':
        return FaInfo;
      case 'system':
        return FaInfo;
      case 'cancellation':
        return FaInfo;
      default:
        return FaInfo;
    }
  };

  // انتخاب رنگ بر اساس نوع فعالیت
  const getActivityColor = (type) => {
    switch (type) {
      case 'appointment':
        return "blue";
      case 'client':
        return "green";
      case 'consultant':
        return "purple";
      case 'cancellation':
        return "red";
      case 'user':
        return "orange";
      case 'payment':
        return "teal";
      default:
        return "gray";
    }
  };

  // تبدیل نوع فعالیت به فارسی
  const getActivityTypeText = (type) => {
    switch (type) {
      case 'appointment':
        return 'ملاقات';
      case 'client':
        return 'مراجع';
      case 'consultant':
        return 'مشاور';
      case 'user':
        return 'کاربر';
      case 'payment':
        return 'پرداخت';
      case 'system':
        return 'سیستم';
      case 'cancellation':
        return 'لغو';
      default:
        return type;
    }
  };

  // تبدیل عملیات به فارسی
  const getActionText = (action) => {
    switch (action) {
      case 'created':
        return 'ایجاد شد';
      case 'updated':
        return 'بروزرسانی شد';
      case 'deleted':
        return 'حذف شد';
      case 'completed':
        return 'تکمیل شد';
      case 'cancelled':
        return 'لغو شد';
      case 'scheduled':
        return 'برنامه‌ریزی شد';
      case 'logged_in':
        return 'ورود';
      case 'logged_out':
        return 'خروج';
      default:
        return action;
    }
  };

  const IconComponent = getActivityIcon(activity.type);
  const iconColor = getActivityColor(activity.type);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center">
            <Box
              p="8px"
              borderRadius="50%"
              bg={`${iconColor}.100`}
              color={`${iconColor}.600`}
              mr="12px"
            >
              <IconComponent size="16px" />
            </Box>
            جزئیات فعالیت
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* اطلاعات اصلی */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" mb="8px">
                {activity.message}
              </Text>
              <HStack spacing={2} mb="12px">
                <Badge colorScheme={iconColor}>
                  {getActivityTypeText(activity.type)}
                </Badge>
                <Badge colorScheme="gray">
                  {getActionText(activity.action)}
                </Badge>
              </HStack>
            </Box>

            <Divider />

            {/* اطلاعات زمانی */}
            <Box>
              <Text fontWeight="semibold" mb="8px" color="gray.600">
                <FaCalendarAlt style={{ display: 'inline', marginLeft: '8px' }} />
                زمان ایجاد
              </Text>
              <Text fontSize="sm">
                <JalaliDateDisplay date={activity.createdAt} showTime={true} />
              </Text>
            </Box>

            {/* کاربر ایجاد کننده */}
            {activity.createdBy && (
              <Box>
                <Text fontWeight="semibold" mb="8px" color="gray.600">
                  <FaUser style={{ display: 'inline', marginLeft: '8px' }} />
                  ایجاد شده توسط
                </Text>
                <Text fontSize="sm">
                  {activity.createdBy.name || 'سیستم'}
                </Text>
              </Box>
            )}

            {/* جزئیات */}
                                {activity.details && Object.keys(activity.details).length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" mb="8px" color="gray.600">
                          <FaCode style={{ display: 'inline', marginLeft: '8px' }} />
                          جزئیات
                        </Text>
                        <Box
                          bg="gray.50"
                          p="12px"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <VStack spacing={2} align="stretch">
                            {Object.entries(activity.details).map(([key, value]) => (
                              <Flex key={key} justify="space-between" align="center">
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                  {key}:
                                </Text>
                                <Text fontSize="sm" color="gray.600" textAlign="left" maxW="60%">
                                  {value || 'نامشخص'}
                                </Text>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      </Box>
                    )}

            {/* اطلاعات اضافی */}
            {activity.ipAddress && (
              <Box>
                <Text fontWeight="semibold" mb="8px" color="gray.600">
                  <FaInfo style={{ display: 'inline', marginLeft: '8px' }} />
                  آدرس IP
                </Text>
                <Text fontSize="sm" fontFamily="mono">
                  {activity.ipAddress}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            بستن
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ActivityDetailModal; 